/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Maybe, ValueOf } from '../types/base/';

export type KeySelector<T> = SelectorFn<T> | keyof T;
export type SelectorFn<T> = (item: T) => SelectorResult<T>;
export type SelectorResult<T> = T[keyof T];

export function is<T>(obj: unknown, test: (o: T) => boolean): obj is T {
  return test(obj as T);
}

export function isArray(payload: unknown): payload is unknown[] {
  return Array.isArray(payload);
}

export function isObject(payload: unknown): payload is Record<string, unknown> {
  return payload !== null && typeof payload === 'object' && !Array.isArray(payload);
}

export type CompareFunction<T> = (payload: unknown) => payload is T;
export function isAll<T>(compareFn: CompareFunction<T>, payloads: unknown[]): payloads is T[] {
  return payloads.every(compareFn);
}

/**
 * Generic comparator for arrays of objects by key or selector function.
 *
 * @example ```ts
 * const listA = [{ id: 1 }, { id: 2 }];
 * const listB = [{ id: 2 }, { id: 1 }];
 * isSameArrayBy(listA, listB, item => item.id) | isSameArrayBy(listA, listB, 'id')
 * => true
 * ```
 */
export function isSameArrayBy<T>(a: T[], b: T[], keySelector: KeySelector<T>): boolean {
  if (a.length !== b.length) return false;
  const selectorFn = getSelectorFn(keySelector);
  const aKeys = a.map(selectorFn).sort();
  const bKeys = b.map(selectorFn).sort();
  for (let i = 0; i < aKeys.length; i++) {
    if (aKeys[i] !== bKeys[i]) return false;
  }

  return true;
}

/**
 * Deep equality check between two values (objects / array / mixed).
 */
export function deepEqual<T>(a: T, b: T): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null || a === undefined || b === undefined) return a === b;

  const values = [a, b];
  if (isAll(isArray, values)) {
    const arrA = a as unknown[];
    const arrB = b as unknown[];
    if (arrA.length !== arrB.length) return false;
    for (let i = 0; i < arrA.length; i++) {
      if (!deepEqual(arrA[i], arrB[i])) return false;
    }

    return true;
  }

  if (isAll(isObject, values)) {
    const objA = a as Record<string, unknown>;
    const objB = b as Record<string, unknown>;
    const aKeys = Object.keys(objA);
    const bKeys = Object.keys(objB);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (!bKeys.includes(key)) return false;
      if (!deepEqual(objA[key], objB[key])) return false;
    }

    return true;
  }

  return false;
}

/**
 * @example  ```ts
 * const list = [{id:1}, {id:2}, {id:3}];
 *  sortBy(list, ({id}) => id) | sortBy(list, 'id')
 *  => [{id:1}, {id:2}, {id:3}]
 * ```
 */
export function sortBy<T>(array: T[], keySelector: KeySelector<T>): T[] {
  const selectorFn = getSelectorFn(keySelector);

  return [...array].sort((a, b) => {
    const ka = selectorFn(a);
    const kb = selectorFn(b);
    if (ka < kb) return -1;
    if (ka > kb) return 1;

    return 0;
  });
}

/**
 * @example ```ts
 * const list = [{id:1, color: 'blue'}, {id:2, color: 'blue'}];
 * groupBy(list, ({color}) => color) | groupBy(list, 'color')
 * => Map(1) { 'blue' => [{ id: 1, color: 'blue' }, { id: 2, color: 'blue' }] }
 * ```
 */
export function groupBy<T>(array: T[], keySelector: KeySelector<T>): Map<any, T[]> {
  const selectorFn = getSelectorFn(keySelector);

  const map = new Map<any, T[]>();
  for (const item of array) {
    const key = selectorFn(item);
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)!.push(item);
  }

  return map;
}

/**
 * @example ```ts
 *  unionBy('id',
 *   [{ id: 1, color: 'blue' }, { id: 2, color: 'blue' }],
 *   [{ id: 1, color: 'red' }, { id: 3, color: 'red' }]
 *  )
 *  => [{ id: 1, color: 'red' }, { id: 2, color: 'blue' }, { id: 3, color: 'red' }]
 * ```
 */
export function unionBy<T>(key: keyof T & string, ...arrays: T[][]): T[] {
  const map = new Map<string, T>();
  arrays.flat().forEach((item) => map.set(String(item[key]), item));

  return Array.from(map.values());
}

/**
 * update only items that changed (updated | deleted)
 *
 * @example ```ts
 * const current = [{ id: 1, color: 'blue' }, { id: 2, color: 'blue' }];
 * const next = [{ id: 1, color: 'red' }, { id: 3, color: 'red' }];
 * updateBy(current, next, 'id')
 * => [{ id: 1, color: 'red' }, { id: 2, color: 'blue' }, { id: 3, color: 'red' }]
 * ```
 */
export function updateBy<T>(current: T[], next: T[], keySelector: KeySelector<T>): T[] {
  const selectorFn = getSelectorFn(keySelector);
  const nextMap = new Map(next.map((item) => [selectorFn(item), item]));
  const currentKeys = new Set(current.map(selectorFn));
  const updated = current.map((item) => {
    const key = selectorFn(item);

    return nextMap.has(key) ? nextMap.get(key)! : item;
  });
  const newItems = next.filter((item) => !currentKeys.has(selectorFn(item)));

  return [...updated, ...newItems];
}

/** handle both key string | callback selector
 *
 * @example ```ts
 * groupBy(list, ({ color }) => color)
 * groupBy(list, 'color')
 * ```
 */
export function getSelectorFn<T>(keySelector: KeySelector<T>): SelectorFn<T> {
  return typeof keySelector === 'function' ? keySelector : (item: T) => item[keySelector];
}

/**
 * Performs a deep merge of objects and returns new object.
 * - (immutable) return copy - do not modify input
 * - dont merge if undefined - but null does erase a value
 *
 * @param {...items} objects[] - Objects to merge
 * @param option Option - last object may be some merge option
 * @returns {object} New object with merged key/values
 */
export function deepMerge<T extends Record<string, unknown>>(...items: any[]): T {
  const { list, mergeOptions } = fetchItemsAndOptions(items);

  return list.reduce((prev, current) => {
    Object.keys(current).forEach((key) => {
      const oldVal = prev[key];
      const newVal = current[key];
      const values = [oldVal, newVal];

      if (isAll(isArray, values)) {
        prev[key] = mergeOptions.mergeArrays
          ? Array.from(new Set(values.flat()))
          : (prev[key] = newVal);
      } else if (isAll(isObject, values)) {
        prev[key] = deepMerge(oldVal, newVal, mergeOptions);
      } else if (newVal !== undefined) {
        prev[key] = newVal;
      }
    });

    return prev;
  }, {} as T);
}

export type MergeOption = {
  mergeArrays?: boolean;
};
function fetchItemsAndOptions(items: any[]): {
  list: any[];
  mergeOptions: MergeOption;
} {
  const lastItem = items.length > 1 && items[items.length - 1];
  const isOption = typeof lastItem.mergeArrays === 'boolean';

  const options: MergeOption = {
    mergeArrays: false,
    ...(isOption ? lastItem : {}),
  };
  const itemsNoOption = isOption ? items.slice(0, -1) : items;

  return {
    mergeOptions: options,
    list: itemsNoOption,
  };
}

type MapFunction<T, U> = (value: T, key: string, index: number) => U;
/** Array.map but for objects - iterate on each keys and you can update values
 * `objectMap(inputObject, (value, key) => `updated key value`);`
 */
export function objectMap<T extends Record<string, unknown>, U>(
  obj: T,
  fn: MapFunction<ValueOf<T>, U>,
): Record<string, U> {
  return Object.fromEntries(Object.entries(obj).map(([k, v], i) => [k, fn(v as ValueOf<T>, k, i)]));
}

type TransformFunction<T, U> = (value: T, key: string, index: number) => U;
/** remap an object to another
 * `objectTransform<InputType, OutputType>(inputObject, ({ id, firstname, lastname }) => ({ id, name: `${firstname} ${lastname}` }));`
 */
export function objectTransform<T extends Record<string, unknown>, U>(
  obj: T,
  fn: TransformFunction<T, U>,
): U {
  return Object.entries(obj).reduce((acc, [key, _value], index) => {
    const mappedValue = fn(obj, key, index);

    return { ...acc, ...mappedValue };
  }, {} as U);
}

/** flatten json so each keys are concat like {menus.applications.links.home:"Accueil", ...} */
export function flattenJSON(
  data: Record<string, any>,
  parentKey = '',
  result: Record<string, string> = {},
): Record<string, string> {
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;
      if (typeof data[key] === 'object' && data[key] !== null) {
        flattenJSON(data[key], newKey, result);
      } else {
        result[newKey] = data[key];
      }
    }
  }

  return result;
}

/**
 * Replaces placeholders in a template string with corresponding values from a params object.
 *
 * Placeholders in the template should be wrapped in double curly braces, e.g., `{{key}}`.
 * If a key in the template does not exist in the params object, it will be replaced with an empty string.
 *
 * Example:
 * ```typescript
 * const template = "Hello, {{name}}!";
 * const params = { name: "John" };
 * const result = replaceParams(template, params); // "Hello, John!"
 * ```
 *
 * @param template - The template string containing placeholders.
 * @param params - An object containing key-value pairs to replace placeholders in the template.
 * @returns The template string with placeholders replaced by corresponding values from the params object.
 */
export function replaceParams(template: string, params: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => params[key] || '');
}

/**
 * @returns uri only if it appears safe enough to avoid XSS attacks
 * @example ```ts
 * class BaseTest { root: string | undefined; }
 * class MyTest extends BaseTest { toto: string | undefined }
 * class MyTest2 extends BaseTest { tata: string | undefined }
 *
 * const myTest = new MyTest();
 * const myTest2 = new MyTest2();
 * const baseTest: BaseTest = myTest2;
 *
 * if(is<MyTest2>(baseTest, o => o.hasOwnProperty('tata'))) {
 *   console.log('is myTest2');
 *   console.log(baseTest.tata); // NOTE: No required cast
 * }
 * ```
 */
export function safeUri(uri?: string | null | undefined): Maybe<string> {
  return uri &&
    (uri.startsWith('/') ||
      uri.startsWith('://') ||
      uri.startsWith('https://') ||
      uri.startsWith('http://localhost'))
    ? uri
    : undefined;
}

/**
 * clamp a value between min and max
 */
export function clamp(min: number, value: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** ensure unique id */
export const uniqueId = (
  (counter) =>
  (str = '') =>
    `${str}${++counter}`
)(0);

/**
 * Returns a shallow copy of an object without the specified key(s).
 * @example without({ a: 1, b: 2 }, 'a') // { b: 2 }
 */
export function without<T extends object, K extends keyof T>(obj: T, ...keys: K[]): Omit<T, K> {
  const result = { ...obj };
  for (const key of keys) {
    delete result[key];
  }

  return result;
}

/**
 * Returns a slugifyed string
 * @example slugify('Vanta Data Viewer') // 'vanta-data-viewer'
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^\w-]+/g, '');
}

/**
 * Returns a debounced wrapper of `func` that delays invocation until `timeout` ms
 * have elapsed since the last call. `timeout` can be a getter for a value that changes over time.
 * Keep a reference to the returned function to add/remove it as an event listener.
 * @example ```ts
 * const debouncedHandler = debounce(() => doSomething(), 300);
 * element.addEventListener('click', debouncedHandler);
 * element.removeEventListener('click', debouncedHandler);
 * ```
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  timeout: number | (() => number) = 0,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | undefined;

  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => func(...args), typeof timeout === 'function' ? timeout() : timeout);
  };
}

/**
 * Returns a throttled wrapper of `func` that invokes at most once every `timeout` ms
 * (leading edge). Useful for high-frequency events like `resize` or `scroll`, where
 * `debounce` would only fire once after the events stop.
 * @example ```ts
 * const throttledHandler = throttle(() => doSomething(), 300);
 * window.addEventListener('resize', throttledHandler);
 * ```
 */
export function throttle<T extends (...args: any[]) => void>(
  func: T,
  timeout = 0,
): (...args: Parameters<T>) => void {
  let lastTime = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastTime >= timeout) {
      lastTime = now;
      func(...args);
    }
  };
}
